import { toBusinessIsoDate } from './businessDate';

const normalizeKey = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const HEADER_ALIASES = {
  transactionDate: ['date', 'transactiondate', 'posteddate', 'purchasedate'],
  storeName: ['storename', 'store', 'merchant', 'vendor', 'payee'],
  storeLocation: ['storelocation', 'location', 'city'],
  itemName: ['itemname', 'item', 'description', 'memo', 'note', 'details', 'name'],
  itemTotal: ['amount', 'itemtotal', 'total', 'debit', 'credit', 'value'],
  category: ['category', 'categoryname', 'budgetcategory'],
  quantity: ['quantity', 'qty'],
  unitPrice: ['unitprice', 'price', 'unitamount'],
  taxAmount: ['tax', 'taxamount', 'salestax'],
  paymentMethod: ['paymentmethod', 'payment', 'account', 'card'],
};

const REQUIRED_FIELDS = ['transactionDate', 'storeName', 'itemTotal'];

const buildHeaderFieldMap = (headerRow) => {
  const normalizedHeaders = headerRow.map((header) => normalizeKey(header));
  return Object.entries(HEADER_ALIASES).reduce((accumulator, [field, aliases]) => {
    const index = normalizedHeaders.findIndex((header) => aliases.includes(header));
    if (index >= 0) {
      accumulator[field] = index;
    }
    return accumulator;
  }, {});
};

const detectDelimiter = (text) => {
  const sample = String(text || '').split(/\r?\n/).find((line) => String(line || '').trim()) || '';
  const candidates = [',', '\t', ';'];
  let best = ',';
  let bestScore = -1;

  candidates.forEach((candidate) => {
    const score = sample.split(candidate).length;
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  });

  return best;
};

export const parseDelimitedText = (text, delimiter = detectDelimiter(text)) => {
  const rows = [];
  let currentCell = '';
  let currentRow = [];
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const nextCharacter = text[index + 1];

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        currentCell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && character === delimiter) {
      currentRow.push(currentCell);
      currentCell = '';
      continue;
    }

    if (!inQuotes && (character === '\n' || character === '\r')) {
      if (character === '\r' && nextCharacter === '\n') {
        index += 1;
      }
      currentRow.push(currentCell);
      rows.push(currentRow);
      currentCell = '';
      currentRow = [];
      continue;
    }

    currentCell += character;
  }

  if (currentCell.length > 0 || currentRow.length > 0) {
    currentRow.push(currentCell);
    rows.push(currentRow);
  }

  return rows
    .map((row) => row.map((cell) => String(cell || '').trim()))
    .filter((row) => row.some((cell) => cell !== ''));
};

const parseCurrencyValue = (value) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return null;
  }

  const isNegativeByParens = /^\(.*\)$/.test(rawValue);
  const normalizedValue = rawValue
    .replace(/^\((.*)\)$/, '$1')
    .replace(/\$/g, '')
    .replace(/,/g, '')
    .trim();
  const parsed = Number(normalizedValue);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  const signedValue = isNegativeByParens ? -parsed : parsed;
  return Number(signedValue.toFixed(2));
};

const parseIntegerValue = (value, fallback = 1) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return fallback;
  }

  const parsed = Number.parseInt(rawValue, 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const parseDateValue = (value) => {
  return toBusinessIsoDate(value);
};

const buildCategoryLookup = (categories = []) => {
  const lookup = new Map();
  const categoriesById = new Map(
    categories.map((category) => [String(category.id || '').trim(), category])
  );

  categories.forEach((category) => {
    const id = String(category.id || '').trim();
    const name = String(category.name || '').trim();
    const parentName = String(
      category.parentCategory?.name ||
      categoriesById.get(String(category.parentCategoryId || '').trim())?.name ||
      ''
    ).trim();
    const displayName = parentName ? `${parentName} > ${name}` : name;
    [id, name, displayName].forEach((candidate) => {
      const key = normalizeKey(candidate);
      if (key && !lookup.has(key)) {
        lookup.set(key, {
          id,
          name,
          displayName,
        });
      }
    });
  });

  return lookup;
};

export const buildHistoricalImportPreview = ({ text, categories = [] }) => {
  const rows = parseDelimitedText(String(text || ''));
  if (rows.length < 2) {
    throw new Error('The file must include a header row and at least one data row.');
  }

  const headerRow = rows[0];
  const fieldMap = buildHeaderFieldMap(headerRow);
  const missingFields = REQUIRED_FIELDS.filter((field) => fieldMap[field] == null);
  if (missingFields.length > 0) {
    throw new Error(`Missing required column headers: ${missingFields.join(', ')}.`);
  }

  const categoryLookup = buildCategoryLookup(categories);
  const previewRows = rows.slice(1).map((row, rowIndex) => {
    const rawCategory = fieldMap.category != null ? String(row[fieldMap.category] || '').trim() : '';
    const matchedCategory = rawCategory ? categoryLookup.get(normalizeKey(rawCategory)) || null : null;
    const transactionDate = parseDateValue(row[fieldMap.transactionDate]);
    const storeName = String(row[fieldMap.storeName] || '').trim();
    const itemName = fieldMap.itemName != null
      ? String(row[fieldMap.itemName] || '').trim()
      : '';
    const itemTotal = parseCurrencyValue(row[fieldMap.itemTotal]);
    const quantity = fieldMap.quantity != null ? parseIntegerValue(row[fieldMap.quantity], 1) : 1;
    const unitPrice = fieldMap.unitPrice != null ? parseCurrencyValue(row[fieldMap.unitPrice]) : null;
    const taxAmount = fieldMap.taxAmount != null ? parseCurrencyValue(row[fieldMap.taxAmount]) : null;
    const paymentMethod = fieldMap.paymentMethod != null ? String(row[fieldMap.paymentMethod] || '').trim() : '';
    const storeLocation = fieldMap.storeLocation != null ? String(row[fieldMap.storeLocation] || '').trim() : '';
    const errors = [];

    if (!transactionDate) {
      errors.push('Invalid or missing date');
    }
    if (!storeName) {
      errors.push('Missing merchant/store');
    }
    if (itemTotal == null || itemTotal === 0) {
      errors.push('Invalid amount');
    }
    if (quantity == null) {
      errors.push('Invalid quantity');
    }
    return {
      rowNumber: rowIndex + 2,
      transactionDate,
      storeName,
      storeLocation,
      itemName: itemName || storeName,
      itemTotal,
      quantity,
      unitPrice,
      taxAmount,
      paymentMethod,
      rawCategory,
      categoryId: matchedCategory?.id || '',
      categoryLabel: matchedCategory?.displayName || '',
      requiresCategoryFallback: Boolean(!matchedCategory),
      errors,
    };
  }).filter((row) => {
    const hasValues = [
      row.transactionDate,
      row.storeName,
      row.itemName,
      row.rawCategory,
      row.paymentMethod,
      row.storeLocation,
      row.itemTotal,
    ].some((value) => String(value ?? '').trim() !== '');
    return hasValues;
  });

  return {
    headers: headerRow,
    rows: previewRows,
    summary: {
      totalRows: previewRows.length,
      validRows: previewRows.filter((row) => row.errors.length === 0).length,
      invalidRows: previewRows.filter((row) => row.errors.length > 0).length,
      unmatchedCategoryRows: previewRows.filter((row) => row.requiresCategoryFallback).length,
    },
  };
};

export const buildHistoricalImportPayload = (previewRows = [], fallbackCategoryId = '') => {
  const normalizedFallbackCategoryId = String(fallbackCategoryId || '').trim();
  const errors = [];
  const transactions = previewRows
    .filter((row) => row && row.errors.length === 0)
    .map((row) => {
      const categoryId = String(row.categoryId || '').trim() || normalizedFallbackCategoryId;
      if (!categoryId) {
        errors.push(`Row ${row.rowNumber} needs a valid category.`);
        return null;
      }

      return {
        transactionDate: row.transactionDate,
        storeName: row.storeName,
        storeLocation: row.storeLocation || undefined,
        itemName: row.itemName,
        itemTotal: row.itemTotal,
        quantity: row.quantity || 1,
        unitPrice: row.unitPrice,
        taxAmount: row.taxAmount,
        paymentMethod: row.paymentMethod || undefined,
        categoryId,
      };
    })
    .filter(Boolean);

  return {
    transactions,
    errors,
  };
};
